import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../axiosConfig';

interface Conversation {
  id: string;
  uuid: string;
  user_identifier: string;
  status: 'in_progress' | 'completed' | 'abandoned';
  started_at: string;
  ended_at: string | null;
  project_id: number;
  updated_at: string;
}

interface SentimentAnalysis {
  sentiment: 'positive' | 'negative' | 'neutral' | 'mixed';
  confidence: number;
  key_phrases: string[];
  explanation: string;
}

interface ConversationSummary {
  summary: string;
  main_topics: string[];
  key_points: string[];
}

interface ConversationState {
  conversations: Conversation[];
  isLoading: boolean;
  error: string | null;
  currentConversationEntries: any[];
  isLoadingEntries: boolean;
  selectedConversation: Conversation | null;
  sentimentAnalysis: SentimentAnalysis | null;
  isLoadingSentiment: boolean;
  conversationSummary: ConversationSummary | null;
  isLoadingSummary: boolean;
  generatedSuggestions: {
    suggestions: Array<{
      idea: string;
      rationale: string;
      impact: string;
    }>;
    message?: string | null;
  } | null;

  isLoadingSuggestions: boolean;
  initialLoadComplete: boolean;
  translatedEntries: any[];
}

const initialState: ConversationState = {
  conversations: [],
  isLoading: false,
  error: null,
  currentConversationEntries: [],
  isLoadingEntries: false,
  selectedConversation: null,
  sentimentAnalysis: null,
  isLoadingSentiment: false,
  conversationSummary: null,
  isLoadingSummary: false,
  generatedSuggestions: null,
  isLoadingSuggestions: false,
  initialLoadComplete: false,
  translatedEntries: [],
};

export type DownloadFormat = 'pdf' | 'docx' | 'csv' | 'json';

export interface DownloadConversationParams {
  projectId: number;
  conversationId: string | null;
  format: DownloadFormat;
}

export interface DownloadAllConversationsParams {
  projectId: number;
  format: DownloadFormat;
}

export const fetchConversations = createAsyncThunk(
  'conversations/fetchConversations',
  async (projectId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/projects/${projectId}/conversations`
      );
      return response.data.conversations;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const fetchConversationEntries = createAsyncThunk(
  'conversations/fetchConversationEntries',
  async (
    {
      conversationId,
      projectIdOrSlug,
    }: { conversationId: string; projectIdOrSlug: string | number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(
        `/conversation-entries/${conversationId}/${projectIdOrSlug}`
      );
      return response.data.entries;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const fetchConversationSentiment = createAsyncThunk(
  'conversations/fetchConversationSentiment',
  async (
    {
      conversationId,
      projectIdOrSlug,
    }: { conversationId: string; projectIdOrSlug: string | number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(
        `/conversation/${conversationId}/conversation-sentiment/${projectIdOrSlug}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const fetchConversationSummary = createAsyncThunk(
  'conversations/fetchConversationSummary',
  async (
    {
      conversationId,
      projectIdOrSlug,
    }: { conversationId: string; projectIdOrSlug: string | number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/projects/${projectIdOrSlug}/conversations/${conversationId}/generate-summary`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const generateSuggestions = createAsyncThunk(
  'conversations/generateSuggestions',
  async (
    {
      projectIdOrSlug,
      conversationId,
      regenerate = false,
    }: {
      projectIdOrSlug: string | number;
      conversationId: string;
      regenerate?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/projects/${projectIdOrSlug}/conversations/${conversationId}/generate-ideas`,
        { regenerate }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const downloadConversation = createAsyncThunk(
  'conversations/downloadConversation',
  async (
    { projectId, conversationId, format }: DownloadConversationParams,
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(
        `/projects/${projectId}/conversations/${conversationId}/export/${format}`,
        { responseType: 'blob' }
      );

      return { data: response.data, headers: response.headers };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const downloadAllConversations = createAsyncThunk(
  'conversations/downloadAllConversations',
  async (
    { projectId, format }: DownloadAllConversationsParams,
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(
        `/projects/${projectId}/all-conversations/export/${format}`,
        { responseType: 'blob' }
      );
      return { data: response.data, headers: response.headers };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const translateConversation = createAsyncThunk(
  'conversations/translateConversation',
  async (
    {
      projectIdOrSlug,
      conversationId,
      targetLanguage,
    }: {
      projectIdOrSlug: string | number;
      conversationId: string;
      targetLanguage: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        `/projects/${projectIdOrSlug}/conversations/${conversationId}/translate`,
        { target_language: targetLanguage }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

const conversationSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    setSelectedConversation: (state, action) => {
      state.selectedConversation = action.payload;
    },
    setInitialLoadComplete: (state) => {
      state.initialLoadComplete = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        
        state.isLoading = false;
        state.conversations = action.payload;
        if (!state.initialLoadComplete) {
          state.initialLoadComplete = true;
        }
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchConversationEntries.pending, (state) => {
        state.isLoadingEntries = true;
        state.currentConversationEntries = [];
        state.error = null;
        state.translatedEntries = [];
      })
      .addCase(fetchConversationEntries.fulfilled, (state, action) => {
      
        state.isLoadingEntries = false;
        state.currentConversationEntries = action.payload;
        // Update the selectedConversation's updated_at time
        if (state.selectedConversation) {
          state.selectedConversation.updated_at = new Date().toISOString();
        }
      })
      .addCase(fetchConversationEntries.rejected, (state, action) => {
        state.isLoadingEntries = false;
        state.error = action.payload as string;
      })
      .addCase(fetchConversationSentiment.pending, (state) => {
        state.isLoadingSentiment = true;
        state.error = null;
      })
      .addCase(fetchConversationSentiment.fulfilled, (state, action) => {
       
        state.isLoadingSentiment = false;
        state.sentimentAnalysis = action.payload;
      })
      .addCase(fetchConversationSentiment.rejected, (state, action) => {
        state.isLoadingSentiment = false;
        state.error = action.payload as string;
      })
      .addCase(fetchConversationSummary.pending, (state) => {
        state.isLoadingSummary = true;
      })
      .addCase(fetchConversationSummary.fulfilled, (state, action) => {
        state.isLoadingSummary = false;
        state.conversationSummary = action.payload.summary;
      })
      .addCase(fetchConversationSummary.rejected, (state, action) => {
        state.isLoadingSummary = false;
        state.error = action.payload as string;
      })
      .addCase(generateSuggestions.pending, (state) => {
        state.isLoadingSuggestions = true;
      })
      .addCase(generateSuggestions.fulfilled, (state, action) => {
        state.isLoadingSuggestions = false;
        state.generatedSuggestions = action.payload;
      })
      .addCase(generateSuggestions.rejected, (state, action) => {
        state.isLoadingSuggestions = false;
        state.error = action.payload as string;
      })
      .addCase(translateConversation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(translateConversation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.translatedEntries = action.payload.translated_entries;
        // You can optionally store the translated entries in the state if needed
      })
      .addCase(translateConversation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedConversation, setInitialLoadComplete } =
  conversationSlice.actions;
export default conversationSlice.reducer;
